const OPTION_SELECTOR = '[data-region-selector-option]';
const PANEL_SELECTOR = '[data-region-selector-panel]';
const FORM_SELECTOR = '.region-selector__localization-form';

/**
 * <region-language-selector>
 *
 * Handles opening/closing the dropdown panels. Each toggle button has
 * `data-region-selector-toggle` and is immediately followed by the panel it
 * controls (marked with `data-region-selector-panel`); clicking it shows/hides
 * that panel and flips `aria-expanded`. IDs are avoided throughout since this
 * snippet is rendered more than once per page (mobile + desktop).
 */
class RegionLanguageSelector extends HTMLElement {
  constructor() {
    super();

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
  }

  connectedCallback() {
    this.toggles = Array.from(
      this.querySelectorAll('[data-region-selector-toggle]')
    );

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', this.onToggleClick);
    });
    this.addEventListener('keydown', this.onKeydown);

    this.bindOptions();
  }

  bindOptions() {
    const options = Array.from(this.querySelectorAll(OPTION_SELECTOR));

    options.forEach((option) => {
      option.addEventListener('click', this.onOptionClick);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  panelFor(toggle) {
    const panel = toggle.nextElementSibling;
    return panel && panel.matches(PANEL_SELECTOR) ? panel : null;
  }

  scopeFor(toggle) {
    return toggle.closest('[data-region-selector-panel]') || this;
  }

  siblingsOf(toggle) {
    const scope = this.scopeFor(toggle);
    return this.toggles.filter(
      (candidate) => candidate !== toggle && this.scopeFor(candidate) === scope
    );
  }

  isOpen(toggle) {
    return toggle.getAttribute('aria-expanded') === 'true';
  }

  open(toggle, panel) {
    toggle.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    document.addEventListener('click', this.onDocumentClick);
  }

  close(toggle, panel) {
    toggle.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
  }

  closeAll() {
    this.toggles.forEach((toggle) => {
      const panel = this.panelFor(toggle);
      if (panel) this.close(toggle, panel);
    });
    document.removeEventListener('click', this.onDocumentClick);
  }

  onToggleClick(event) {
    event.stopPropagation();

    const toggle = event.currentTarget;
    const panel = this.panelFor(toggle);
    if (!panel) return;

    if (this.isOpen(toggle)) {
      this.close(toggle, panel);
      return;
    }

    this.siblingsOf(toggle).forEach((sibling) => {
      const siblingPanel = this.panelFor(sibling);
      if (siblingPanel) this.close(sibling, siblingPanel);
    });
    this.open(toggle, panel);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.closeAll();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const openToggle = this.toggles.find((toggle) => this.isOpen(toggle));
    this.closeAll();
    (openToggle || this.toggles[0])?.focus();
  }

  onOptionClick(event) {
    const option = event.currentTarget;
    const form = this.querySelector(FORM_SELECTOR);
    const input = form?.querySelector(`input[name="${option.name}"]`);

    if (!input) return;

    input.value = option.value;
    form.requestSubmit();
  }
}

if (!customElements.get('region-language-selector')) {
  customElements.define('region-language-selector', RegionLanguageSelector);
}